/**
 * ClientEventViewModal Component
 * View-only modal for displaying event and task details in client portal
 */

import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

import { useClientCalendarUI } from '../context/ClientCalendarUIContext';
import { useSingleEvent } from 'modules/calendarv2/hooks/useSingleEvent';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { EventDetailsView, TaskDetailsView } from 'modules/calendarv2/components/EventModals/ViewComponents';
import { ModalHeader } from 'shared/ModalHeader';

const ClientEventViewModal = () => {
  const { modals, selectedEventId, selectedEventMetadata, closeEventViewModal } = useClientCalendarUI();
  const { userDetails } = useUserDetailsContext();

  // Fetch event/task data from API
  const {
    data: itemData,
    isLoading,
    isError,
    error,
  } = useSingleEvent(
    selectedEventId,
    selectedEventMetadata,
    !!selectedEventId // Only fetch when we have an ID
  );

  // Determine if this is an event or task based on res_type
  const isTask = itemData?.res_type === 'task';
  const isEvent = itemData?.res_type === 'event';

  // Loading state
  if (modals.eventView && isLoading) {
    return (
      <Dialog
        header={ModalHeader}
        visible={true}
        onHide={closeEventViewModal}
        style={{ width: '600px' }}
        className="new-task-popup media-popup event-view-modal"
        modal
        footer={<></>}
      >
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3 text-muted">Fetching details...</p>
        </div>
      </Dialog>
    );
  }

  // Error state
  if (modals.eventView && isError) {
    return (
      <Dialog
        header={ModalHeader}
        visible
        onHide={closeEventViewModal}
        style={{ width: '600px' }}
        className="new-task-popup media-popup event-view-modal"
        modal
        footer={<></>}
      >
        <div className="text-center p-4">
          <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#f59e0b' }}></i>
          <p className="mt-3">
            {error?.response?.status === 404 ? 'Item not found. It may have been deleted.' : 'Unable to load details. Please try again.'}
          </p>
          {error?.message && <p className="text-muted small mt-2">{error.message}</p>}
        </div>
        <div className="d-flex justify-content-end">
          <Button label="Close" onClick={closeEventViewModal} className="p-button p-button-secondary" />
        </div>
      </Dialog>
    );
  }

  // Don't render if modal is not open or no data
  if (!modals.eventView || !itemData) {
    return null;
  }

  // Transform API data for event display
  const displayEvent = isEvent
    ? {
        id: itemData.pk || selectedEventId,
        title: itemData.title || itemData.event_title || 'Untitled Event',
        start: itemData.meeting_start_time || itemData.when,
        end: itemData.meeting_end_time || itemData.till,
        allDay: itemData.all_day || false,
        extendedProps: {
          ...itemData,
          eventType: itemData.event_type,
          location: itemData.location,
          description: itemData.description,
          isFirmWide: itemData.is_firm_event || itemData.firm_wide || false,
          isRecurring: itemData.is_recurring || false,
          practiceArea: itemData.practice_area,
          caseId: itemData.case_id,
          case_name: itemData.case_name,
          conference_url: itemData.conference_url || itemData.conferencing_url,
          conference_room_id: itemData.conference_room_id,
          conference_room_name: itemData.conference_room_name,
          firm_users: itemData.firm_users || [],
          contacts: itemData.contacts || [],
          groups: itemData.groups || [],
          guests: itemData.guests || itemData.firm_users || itemData.contacts || [],
          attachments: itemData.attachments || [],
          reminder: itemData.reminder || [],
          recurrence_keys: itemData.recurrence_keys,
          created_by: itemData.created_by,
          created_by_name:
            itemData.created_by_name ||
            (itemData.created_by_details
              ? `${itemData.created_by_details?.first_name} ${itemData.created_by_details?.last_name}`
              : 'Unknown'),
          created_by_details: itemData.created_by_details,
          created_by_firm_user_id: itemData.created_by_firm_user_id,
          created_at: itemData.created_at,
          updated_at: itemData.updated_at,
        },
      }
    : null;

  return (
    <Dialog
      visible={modals.eventView}
      onHide={closeEventViewModal}
      style={{ width: '700px', maxHeight: '90vh' }}
      className="new-task-popup media-popup event-view-modal"
      footer={<></>}
      header={ModalHeader}
      resizable={false}
      modal
    >
      {/* Modal Heading - Simple, no action menu for client view */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="modal-heading F-size16 text-bold">{isEvent ? 'Event Details' : isTask ? 'Task Details' : 'Details'}</div>
      </div>

      {/* Event Details */}
      {isEvent && <EventDetailsView event={displayEvent} timezone={userDetails?.timezone} isClientCalendar={true} />}

      {/* Task Details */}
      {isTask && <TaskDetailsView task={itemData} timezone={userDetails?.timezone} isClientCalendar={true} />}

      {/* Unknown Type */}
      {!isEvent && !isTask && (
        <div className="text-center p-4">
          <i className="pi pi-info-circle" style={{ fontSize: '2rem', color: '#6b7280' }}></i>
          <p className="mt-3">Unknown item type: {itemData.res_type}</p>
        </div>
      )}
    </Dialog>
  );
};

export default ClientEventViewModal;
